<script setup lang="ts">
/**
 * Skeleton para Store na tab "Todos" - seções por tipo de moeda
 * Imita o layout com header (ícone + título + ver mais) + cards com scroll horizontal
 * Uso: <SmarticoApostatudoSkeletonStoreGroupsSkeleton :groups="4" :cards-per-group="5" />
 */

interface Props {
  /** Quantidade de grupos skeleton (Promocionais, Coins, Diamonds, Gems) */
  groups?: number;
  /** Quantidade de cards por grupo */
  cardsPerGroup?: number;
  /** Altura do card em pixels (desktop) */
  cardHeight?: number;
  /** Altura do card em pixels (mobile) */
  cardHeightMobile?: number;
  /** Largura do card em pixels (desktop) */
  cardWidth?: number;
  /** Largura do card em pixels (mobile) */
  cardWidthMobile?: number;
}

withDefaults(defineProps<Props>(), {
  groups: 4,
  cardsPerGroup: 5,
  cardHeight: 270,
  cardHeightMobile: 200,
  cardWidth: 208,
  cardWidthMobile: 160,
});
</script>

<template>
  <div class="store-groups-skeleton">
    <div
      v-for="g in groups"
      :key="`group-${g}`"
      class="skeleton-group"
    >
      <!-- Header do grupo (igual ao StoreV2) -->
      <div class="skeleton-group-header">
        <div class="skeleton-header-left">
          <div class="skeleton-icon" />
          <div class="skeleton-title" />
        </div>
        <div class="skeleton-header-right">
          <div class="skeleton-see-more" />
        </div>
      </div>

      <!-- Scroll horizontal de cards -->
      <div class="skeleton-scroll-container">
        <div
          v-for="c in cardsPerGroup"
          :key="`card-${g}-${c}`"
          class="skeleton-card"
          :style="{
            '--card-height': `${cardHeight}px`,
            '--card-height-mobile': `${cardHeightMobile}px`,
            '--card-width': `${cardWidth}px`,
            '--card-width-mobile': `${cardWidthMobile}px`,
          }"
        >
          <!-- Imagem do produto -->
          <div class="skeleton-card-image" />
          <!-- Info do produto -->
          <div class="skeleton-card-info">
            <div class="skeleton-card-title" />
            <div class="skeleton-card-price" />
          </div>
        </div>
      </div>

      <!-- Dots de paginação (mobile) -->
      <div class="skeleton-dots">
        <div
          v-for="d in 3"
          :key="`dot-${g}-${d}`"
          class="skeleton-dot"
          :class="{ active: d === 1 }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.store-groups-skeleton {
  @apply flex flex-col;
}

.skeleton-group {
  @apply mb-6;
}

/* Header do grupo */
.skeleton-group-header {
  @apply flex items-center justify-between mb-3 sm:mb-4 rounded-md;
  height: 32px;
  background: linear-gradient(
    270deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0) 100%
  );
}

@media (min-width: 640px) {
  .skeleton-group-header {
    height: 45px;
  }
}

.skeleton-header-left {
  @apply flex items-center gap-2 sm:gap-4;
}

.skeleton-icon {
  @apply flex-shrink-0 rounded-lg;
  width: 32px;
  height: 32px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 640px) {
  .skeleton-icon {
    width: 45px;
    height: 45px;
  }
}

.skeleton-title {
  @apply rounded;
  height: 14px;
  width: 70px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 640px) {
  .skeleton-title {
    height: 20px;
    width: 112px;
  }
}

.skeleton-header-right {
  @apply flex items-center gap-2 pr-3;
}

.skeleton-see-more {
  @apply h-4 w-20 rounded;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Scroll container */
.skeleton-scroll-container {
  @apply flex gap-3 overflow-hidden pb-2;
}

.skeleton-card {
  @apply flex-shrink-0 rounded-lg flex flex-col overflow-hidden;
  background: rgba(255, 255, 255, 0.03);
  height: var(--card-height-mobile);
  width: var(--card-width-mobile);
  min-width: var(--card-width-mobile);
}

@media (min-width: 640px) {
  .skeleton-card {
    height: var(--card-height);
    width: var(--card-width);
    min-width: var(--card-width);
  }
}

.skeleton-card-image {
  @apply flex-1;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-card-info {
  @apply p-3 flex flex-col gap-2;
}

.skeleton-card-title {
  @apply h-4 w-3/4 rounded;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-card-price {
  @apply h-3 w-1/2 rounded;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Dots de paginação */
.skeleton-dots {
  @apply flex justify-center items-center gap-2 mt-3;

  @media (min-width: 768px) {
    @apply hidden;
  }
}

.skeleton-dot {
  @apply w-2 h-2 rounded-full;
  background: rgba(255, 255, 255, 0.2);
}

.skeleton-dot.active {
  background: rgba(255, 255, 255, 0.5);
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
